import { useEffect, useState, ChangeEvent, FormEvent, useMemo } from "react";
import { useDispatch } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { resources } from "@constants/hardcoded-strings";
import { USER_ROLE_TYPE_OPTIONS } from "@constants/user-role-type-options";
import { AxnAdminUserPropertyBundleTypes } from "@enums/axn-admin-user-property-bundle-types";
import { ButtonTypes } from "@enums/button-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { OrganizationUserInvitationMethodTypes } from "@enums/organization-user-invitation-method-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { RelyingPartyRole, SelectOption } from "@idw-types/common";
import { AxnCompany, AxnIdentityProvider, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { IDWSession } from "@idw-types/data/idw-session";
import { OrganizationConfig } from "@idw-types/data/organization-config";
import { Auth0IdentityProvider } from "@idw-types/data/reference-data/generic";
import { sendUserInvitationReduxRequest } from "@redux-requests/organization-invitations";
import { getOrganizationConfig } from "@requests/axnadmin-core";
import { getRelyingPartyRolesWithPermissions } from "@utils/permissions-util";
import { getAllowedRelyingPartiesForOrganizationId } from "@utils/validation/organization/organization-users/organization-users-util";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { RelyingPartyRolesContainer } from "@containers/common/relying-party-roles-container/relying-party-roles-container";
import { createUserReduxRequest } from "@redux-requests/organization-users";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";

type AddOrganizationUserModalProps = {
    onModalClose: () => void;
    session: IDWSession;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnCompanies: Array<AxnCompany>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    shouldReloadUsers: boolean;
    shouldReloadInvitations: boolean;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
};

type OrganizationConfigState = {
    selectedOrganizationConfig: OrganizationConfig;
    requestInProgress: boolean;
};

type AddOrganizationUserModalState = {
    firstName: string;
    lastName: string;
    email: string;
    credential: string;
    apiUserName: string;
    apiPassword: string;
    selectedOrganization: AxnCompany;
    selectedRoleTypeOption: SelectOption;
    selectedOrganizationRole: AxnAdminUserPropertyBundle;
    selectedRelyingPartyRoles: Array<RelyingPartyRole>;
    selectedInvitationMethodOption: SelectOption<OrganizationUserInvitationMethodTypes>;
};

export const AddOrganizationUserModal = (props: AddOrganizationUserModalProps) => {
    const dispatch = useDispatch();
    const {
        onModalClose,
        session,
        auth0IdentityProviders,
        axnCompanies,
        axnAdminUserPropertyBundles,
        axnRelyingParties,
        axnIdentityProviders,
        shouldReloadUsers,
        shouldReloadInvitations,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties
    } = props;

    const roleTypeOptions = useMemo(() => {
        if (axnAdminUserPropertyBundles.some(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION)) {
            return USER_ROLE_TYPE_OPTIONS;
        }
        return [];
    }, [axnAdminUserPropertyBundles]);

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddOrganizationUserModalState>({
        firstName: "",
        lastName: "",
        email: "",
        credential: "",
        apiUserName: "",
        apiPassword: "",
        selectedOrganization: axnCompanies.length === 1 ? axnCompanies[0] : null,
        selectedRoleTypeOption: roleTypeOptions.length > 0
            ? USER_ROLE_TYPE_OPTIONS[0]
            : USER_ROLE_TYPE_OPTIONS[1],
        selectedOrganizationRole: roleTypeOptions.length > 0
            ? axnAdminUserPropertyBundles.filter(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION)[0] || null
            : null,
        selectedRelyingPartyRoles: [],
        selectedInvitationMethodOption: null
    });
    const [organizationConfigState, setOrganizationConfigState] = useState<OrganizationConfigState>({
        requestInProgress: false,
        selectedOrganizationConfig: null
    });
    const [requestInProgress, setRequestInProgress] = useState(false);

    const getSelectedOrganizationConfig = async () => {
        setOrganizationConfigState({
            ...organizationConfigState,
            requestInProgress: true
        });

        const companyConfig = await getOrganizationConfig(state.selectedOrganization.id);

        if (companyConfig.data) {
            setOrganizationConfigState({
                selectedOrganizationConfig: companyConfig.data,
                requestInProgress: false
            });
        } else if (companyConfig.error) {
            //show error
        }
    };

    useEffect(() => {
        if (state.selectedOrganization) {
            getSelectedOrganizationConfig();
        }
    }, [state.selectedOrganization]);

    const organizationUserInvitationMethodTypeOptions: Array<SelectOption<OrganizationUserInvitationMethodTypes>> = useMemo(() => {
        const optionsToReturn: Array<SelectOption<OrganizationUserInvitationMethodTypes>> = [];
        if (organizationConfigState.selectedOrganizationConfig) {
            if (organizationConfigState.selectedOrganizationConfig.loginServiceUsesAuth0) {
                //has auth0IDP
                optionsToReturn.push({ label: "Invite User - AXN Admin Login", value: OrganizationUserInvitationMethodTypes.INVITE });
                if (session.userPermissions.canCreateAPIUser) {
                    optionsToReturn.push({ label: "Create a Service Account User", value: OrganizationUserInvitationMethodTypes.CREATE });
                }
                return optionsToReturn;
            } else if (organizationConfigState.selectedOrganizationConfig.loginServiceIDPID) {
                //has corporate login
                optionsToReturn.push({ label: "Invite User - Federated SSO", value: OrganizationUserInvitationMethodTypes.INVITE });
                if (session.userPermissions.canCreateAPIUser) {
                    optionsToReturn.push({ label: "Create User - No Invitation", value: OrganizationUserInvitationMethodTypes.CREATE });
                }
            } else {
                //has no corporate login
                optionsToReturn.push({ label: "Invite User", value: OrganizationUserInvitationMethodTypes.INVITE });
            }
        }

        return optionsToReturn;
    }, [organizationConfigState.selectedOrganizationConfig, auth0IdentityProviders, session.userPermissions.canCreateAPIUser]);

    useEffect(() => {
        setState({
            ...state,
            selectedInvitationMethodOption: organizationUserInvitationMethodTypeOptions[0] || null
        });
    }, [organizationUserInvitationMethodTypeOptions]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);

        if (shouldUseCreateUserRoute) {
            let emailToUse = state.email;
            let credentialToUse = state.email;
            let idpToUse = organizationConfigState.selectedOrganizationConfig?.loginServiceIDPID;

            if (state.selectedInvitationMethodOption.value === OrganizationUserInvitationMethodTypes.CREATE) {
                emailToUse = null;
                credentialToUse = null;
                idpToUse = null;
            }

            const response = await createUserReduxRequest(dispatch)(
                state.firstName,
                state.lastName,
                emailToUse,
                credentialToUse,
                state.apiUserName,
                state.apiPassword,
                state.selectedOrganization,
                idpToUse,
                state.selectedOrganizationRole,
                state.selectedRelyingPartyRoles,
                shouldReloadUsers,
                shouldReloadInvitations,
                axnCompanies,
                axnIdentityProviders,
                axnAdminUserPropertyBundles,
                axnRelyingParties,
                axnAdminAvailableUserOrganizationProperties,
                axnAdminAvailableUserRPProperties
            );

            if (response) {
                onModalClose();
            } else {
                setRequestInProgress(false);
            }
        } else {
            await sendUserInvitationReduxRequest(dispatch)(
                state.email,
                state.selectedOrganization,
                state.selectedOrganizationRole,
                state.selectedRelyingPartyRoles,
                shouldReloadInvitations,
                axnAdminUserPropertyBundles,
                axnCompanies,
                axnRelyingParties
            );
            onModalClose();
        }
    };

    const handleUpdateFirstName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            firstName: event.target.value
        });
    };

    const handleUpdateLastName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            lastName: event.target.value
        });
    };

    const handleUpdateEmail = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            email: event.target.value
        });
    };

    const handleUpdateApiUserName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            apiUserName: event.target.value
        });
    };

    const handleUpdateApiPassword = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            apiPassword: event.target.value
        });
    };

    const handleUpdateOrganization = (newValue: SelectOption) => {
        const newOrganization = axnCompanies.find(company => company.id === newValue.value);
        if (newOrganization) {
            let organizationRole = state.selectedOrganizationRole;
            //if there is no selected role and the roleType is org, pick the first possible role
            if (
                !state.selectedOrganizationRole &&
                state.selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION
            ) {
                const filteredBundles = axnAdminUserPropertyBundles.filter(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION);
                organizationRole = filteredBundles[0];
            }
            setState({
                ...state,
                selectedOrganizationRole: organizationRole,
                selectedOrganization: newOrganization,
                email: "",
                firstName: "",
                lastName: "",
                credential: "",
                apiUserName: "",
                apiPassword: ""
            });
        }
    };

    const handleUpdateIWantTo = (newValue: SelectOption<OrganizationUserInvitationMethodTypes>) => {
        setState({
            ...state,
            selectedInvitationMethodOption: newValue,
            email: "",
            credential: "",
            apiUserName: "",
            apiPassword: ""
        });
    };

    const handleRoleTypeChange = (newValue: SelectOption) => {
        const roleBundles = axnAdminUserPropertyBundles.filter(bundle => bundle.type === newValue.value);
        let newAxnAdminUserPropertyBundle = null;
        if (newValue.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && roleBundles.length > 0) {
            newAxnAdminUserPropertyBundle = roleBundles[0];
        }

        setState({
            ...state,
            selectedRoleTypeOption: newValue,
            selectedOrganizationRole: newAxnAdminUserPropertyBundle,
            selectedRelyingPartyRoles: []
        });
    };

    const handleUpdateOrganizationRole = (newValue: SelectOption) => {
        const role = axnAdminUserPropertyBundles.find(bundle => bundle.shortName === newValue.value);

        if (role) {
            setState({
                ...state,
                selectedOrganizationRole: role
            });
        }
    };

    const addRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setState({
            ...state,
            selectedRelyingPartyRoles: [...state.selectedRelyingPartyRoles, newRelyingPartyRole]
        });
    };

    const removeRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setState({
            ...state,
            selectedRelyingPartyRoles: state.selectedRelyingPartyRoles.filter(rpr => rpr.relyingParty.id !== newRelyingPartyRole.relyingParty.id)
        });
    };

    const updateRelyingPartyRole = (newRelyingPartyRole: RelyingPartyRole) => {
        setState({
            ...state,
            selectedRelyingPartyRoles: state.selectedRelyingPartyRoles.map(rpr => {
                if (rpr.relyingParty.id === newRelyingPartyRole.relyingParty.id) {
                    return newRelyingPartyRole
                }
                return rpr;
            })
        })
    };

    const selectedOrganizationOption = useMemo(() => {
        if (state.selectedOrganization) {
            return {
                label: state.selectedOrganization.name,
                value: state.selectedOrganization.id
            };
        }

        return null;
    }, [state.selectedOrganization]);

    const axnAdminUserPropertyBundleOptions = useMemo(() => {
        return axnAdminUserPropertyBundles
            .filter(bundle => bundle.type === state.selectedRoleTypeOption.value)
            .map(bundle => ({ label: bundle.name, value: bundle.shortName }));
    }, [axnAdminUserPropertyBundles, state.selectedRoleTypeOption]);

    const selectedOrganizationRoleOption = useMemo(() => {
        if (state.selectedOrganizationRole) {
            return {
                label: state.selectedOrganizationRole.name,
                value: state.selectedOrganizationRole.shortName
            };
        }
        return null;
    }, [state.selectedOrganizationRole]);

    const organizationAxnRelyingParties = useMemo(() => {
        return getAllowedRelyingPartiesForOrganizationId(
            axnRelyingParties,
            state.selectedOrganization?.id
        );
    }, [axnRelyingParties, state.selectedOrganization]);

    const relyingPartyRolesWithPermissions = useMemo(() => {
        return getRelyingPartyRolesWithPermissions(
            session,
            null,
            organizationAxnRelyingParties,
            state.selectedRelyingPartyRoles
        );
    }, [
        state.selectedRelyingPartyRoles,
        organizationAxnRelyingParties,
        session
    ]);

    const hasErrorWithRelyingPartyRoles = useMemo(() => {
        return (
            state.selectedOrganization &&
            state.selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.RP &&
            state.selectedRelyingPartyRoles.length === 0
        );
    }, [
        state.selectedOrganization,
        state.selectedRoleTypeOption,
        state.selectedRelyingPartyRoles
    ]);

    const shouldUseCreateUserRoute = useMemo(() => {
        if (organizationConfigState.selectedOrganizationConfig) {
            return (
                organizationConfigState.selectedOrganizationConfig.loginServiceUsesAuth0 ||
                (organizationConfigState.selectedOrganizationConfig.loginServiceIDPID &&
                    state.selectedInvitationMethodOption?.value === OrganizationUserInvitationMethodTypes.CREATE
                )
            );
        }

        return false;
    }, [
        organizationConfigState.selectedOrganizationConfig,
        state.selectedInvitationMethodOption
    ]);

    const resource = resources.modals.addOrganizationUser;

    const buttonText = useMemo(() => {
        if (state.selectedInvitationMethodOption?.value === OrganizationUserInvitationMethodTypes.CREATE) {
            return resources.common.buttons.create.label;
        } else {
            return resources.common.buttons.invite.label
        }
    }, [state.selectedInvitationMethodOption]);

    return (
        <DefaultModal
            automationId="add-organization-user-modal"
            ariaLabelledBy="add-organization-user-title"
            onHide={onModalClose}
            backdrop={"static"}
            size={ModalSizeTypes.EXTRA_LARGE}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-organization-user-title"} closeButton>
                    <Modal.Title>{resource.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {axnCompanies.length > 1 && (
                        <OrganizationSelectFormGroup
                            idPrefix="invitation"
                            onChange={handleUpdateOrganization}
                            axnCompanies={axnCompanies}
                            selectedOption={selectedOrganizationOption}
                            disabled={requestInProgress || axnCompanies.length === 1 || organizationConfigState.requestInProgress}
                            isRequired={true}
                            isSearchable={true}
                            isClearable={false}
                            labelWidth={labelWidth}
                        />
                    )}
                    <SelectFormGroup
                        controlId={`add-organization-user-iwantto-select`}
                        label={resource.inputs.iWantTo.label}
                        ariaLabel={resource.inputs.iWantTo.label}
                        automationId={`add-organization-user-iwantto-select`}
                        onChange={handleUpdateIWantTo}
                        options={organizationUserInvitationMethodTypeOptions}
                        selectedOption={state.selectedInvitationMethodOption}
                        isLoading={organizationConfigState.requestInProgress}
                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                        isRequired={true}
                        isSearchable={false}
                        isClearable={false}
                        labelWidth={labelWidth}
                    />
                    {state.selectedOrganization && (
                        <>
                            <hr />
                            {shouldUseCreateUserRoute && (
                                <>
                                    <InputFormGroup
                                        controlId="add-organization-user-first-name-input"
                                        label={resource.inputs.firstName.label}
                                        ariaLabel={resource.inputs.firstName.label}
                                        automationId="add-organization-user-first-name-input"
                                        placeholder={resource.inputs.firstName.placeholder}
                                        value={state.firstName}
                                        onChange={handleUpdateFirstName}
                                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                                        isRequired={true}
                                        labelWidth={labelWidth}
                                    />
                                    <InputFormGroup
                                        controlId="add-organization-user-last-name-input"
                                        label={resource.inputs.lastName.label}
                                        ariaLabel={resource.inputs.lastName.label}
                                        automationId="add-organization-user-last-name-input"
                                        placeholder={resource.inputs.lastName.placeholder}
                                        value={state.lastName}
                                        onChange={handleUpdateLastName}
                                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                                        isRequired={true}
                                        labelWidth={labelWidth}
                                    />
                                </>
                            )}
                            {state.selectedInvitationMethodOption?.value === OrganizationUserInvitationMethodTypes.INVITE && (
                                <InputFormGroup
                                    controlId="email-input"
                                    label={resource.inputs.email.label}
                                    ariaLabel={resource.inputs.email.label}
                                    automationId="email-input"
                                    placeholder={resource.inputs.email.placeholder}
                                    value={state.email}
                                    type={"email"}
                                    onChange={handleUpdateEmail}
                                    disabled={requestInProgress || organizationConfigState.requestInProgress}
                                    isRequired={true}
                                    labelWidth={labelWidth}
                                />
                            )}
                            {state.selectedInvitationMethodOption?.value === OrganizationUserInvitationMethodTypes.CREATE && (
                                <>
                                    <InputFormGroup
                                        controlId="add-organization-user-api-username-input"
                                        label={resource.inputs.apiUserName.label}
                                        ariaLabel={resource.inputs.apiUserName.label}
                                        automationId="add-organization-user-api-username-input"
                                        placeholder={resource.inputs.apiUserName.placeholder}
                                        value={state.apiUserName}
                                        onChange={handleUpdateApiUserName}
                                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                                        isRequired={true}
                                        labelWidth={labelWidth}
                                    />
                                    <InputFormGroup
                                        controlId="add-organization-user-api-password-input"
                                        label={resource.inputs.apiPassword.label}
                                        ariaLabel={resource.inputs.apiPassword.label}
                                        automationId="add-organization-user-api-password-input"
                                        placeholder={resource.inputs.apiPassword.placeholder}
                                        value={state.apiPassword}
                                        type={"password"}
                                        onChange={handleUpdateApiPassword}
                                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                                        isRequired={true}
                                        labelWidth={labelWidth}
                                    />
                                </>
                            )}
                            {state.selectedOrganization && roleTypeOptions.length > 0 && (
                                <SelectFormGroup
                                    controlId={"user-role-type"}
                                    automationId={"user-role-type"}
                                    label={resource.inputs.roleType.label}
                                    ariaLabel={resource.inputs.roleType.label}
                                    disabled={requestInProgress || organizationConfigState.requestInProgress}
                                    isClearable={false}
                                    isRequired={true}
                                    isSearchable={false}
                                    labelWidth={labelWidth}
                                    onChange={handleRoleTypeChange}
                                    options={roleTypeOptions}
                                    selectedOption={state.selectedRoleTypeOption}
                                />
                            )}
                            {state.selectedOrganization && state.selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && (
                                <>
                                    <SelectFormGroup
                                        controlId={`organization-role-select`}
                                        label={resource.inputs.role.label}
                                        ariaLabel={resource.inputs.role.label}
                                        automationId={`organization-role-select`}
                                        onChange={handleUpdateOrganizationRole}
                                        options={axnAdminUserPropertyBundleOptions}
                                        selectedOption={selectedOrganizationRoleOption}
                                        disabled={requestInProgress || organizationConfigState.requestInProgress}
                                        isRequired={true}
                                        isSearchable={false}
                                        isClearable={false}
                                        labelWidth={labelWidth}
                                    />
                                    <LabelValueContainer
                                        controlId={`organization-user-role-description`}
                                        label={" "}
                                        labelIsBold={false}
                                        labelWidth={LabelWidthTypes.MEDIUM}
                                        layout={LayoutTypes.ROW}
                                        value={state.selectedOrganizationRole?.description}
                                    />
                                </>
                            )}
                            {state.selectedOrganization && state.selectedRoleTypeOption.value === AxnAdminUserPropertyBundleTypes.RP && (
                                <>
                                    <RelyingPartyRolesContainer
                                        isDisabled={requestInProgress || organizationConfigState.requestInProgress}
                                        requiredUserPermissionType={UserPermissionTypes.CREATE_USERS}
                                        sessionUserHasAccessToAllAdminUserRPs={true}
                                        relyingPartyRolesWithPermissions={relyingPartyRolesWithPermissions}
                                        axnRelyingParties={organizationAxnRelyingParties}
                                        axnAdminUserPropertyBundles={axnAdminUserPropertyBundles}
                                        axnAdminUserPropertyBundleOptions={axnAdminUserPropertyBundleOptions}
                                        labelWidth={labelWidth}
                                        addRelyingPartyRole={addRelyingPartyRole}
                                        removeRelyingPartyRole={removeRelyingPartyRole}
                                        updateRelyingPartyRole={updateRelyingPartyRole}
                                    />
                                    {state.selectedRelyingPartyRoles.length === 0 && (
                                        <Alert variant={"danger"}>
                                            {resource.noRelyingPartyRolesError}
                                        </Alert>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-organization-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <ButtonWithSpinner
                        id="add-organization-submit-button"
                        disabled={requestInProgress || hasErrorWithRelyingPartyRoles || organizationConfigState.requestInProgress}
                        hideSpinner={hasErrorWithRelyingPartyRoles || organizationConfigState.requestInProgress}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={buttonText}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};